import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  doCancelSubscription,
  doCloseCardFailureError,
} from 'ducks/billing/operations'
import { selectCardError } from 'ducks/billing/selectors'
import { selectBillingUsage } from 'ducks/billing/selectors/selectBillingUsage'
import { isPricingSmaller, isLimitsExceeded } from 'ducks/billing/util'

const emptyModalState = {
  downgradeKb: false,
  downgradeInbox: false,
  cancellation: false,
  limitKb: false,
  limitInbox: false,
}

export const useBillingModals = ({ onSave }) => {
  const dispatch = useDispatch()
  const usage = useSelector(selectBillingUsage)
  const [modals, setModals] = useState(emptyModalState)
  const cardError = useSelector(selectCardError)
  const [lastChanges, setLastChanges] = useState({})

  const cancelSave = useCallback(() => {
    setModals(emptyModalState)
  }, [])

  const saveChanges = useCallback(
    inputChanges => {
      const changes = inputChanges.form ? inputChanges : lastChanges
      setModals({
        downgradeKb: false,
        downgradeInbox: false,
        cancellation: false,
        limitKb: false,
        limitInbox: false,
      })
      if (onSave) onSave(changes.form)
      setLastChanges({})
    },
    [onSave, lastChanges]
  )

  const checkKbAndSave = useCallback(
    inputChanges => {
      const changes = inputChanges.form ? inputChanges : lastChanges
      const { currentKbPlan, newKbPlan } = changes

      if (
        currentKbPlan.planId !== newKbPlan.planId &&
        isPricingSmaller(currentKbPlan, newKbPlan)
      ) {
        setModals({
          downgradeKb: true,
          downgradeInbox: false,
          cancellation: false,
          limitKb: false,
          limitInbox: false,
        })
      } else {
        saveChanges(changes)
      }
    },
    [saveChanges, lastChanges]
  )

  const checkInboxAndSave = useCallback(
    inputChanges => {
      const changes = inputChanges.form ? inputChanges : lastChanges
      const { currentInboxPlan, newInboxPlan } = changes

      if (
        currentInboxPlan.planId !== newInboxPlan.planId &&
        isPricingSmaller(currentInboxPlan, newInboxPlan)
      ) {
        setModals({
          downgradeKb: false,
          downgradeInbox: true,
          cancellation: false,
          limitKb: false,
          limitInbox: false,
        })
      } else {
        checkKbAndSave(changes)
      }
    },
    [checkKbAndSave, lastChanges]
  )

  const checkKbLimitAndSave = useCallback(
    inputChanges => {
      const changes = inputChanges.form ? inputChanges : lastChanges
      const { currentKbPlan, newKbPlan } = changes

      if (
        currentKbPlan.planId !== newKbPlan.planId &&
        isPricingSmaller(currentKbPlan, newKbPlan) &&
        isLimitsExceeded(newKbPlan, usage.kb)
      ) {
        setModals({
          downgradeKb: false,
          downgradeInbox: false,
          cancellation: false,
          limitKb: true,
          limitInbox: false,
        })
      } else {
        checkInboxAndSave(changes)
      }
    },
    [usage, checkInboxAndSave, lastChanges]
  )

  const checkInboxLimitAndSave = useCallback(
    inputChanges => {
      const changes = inputChanges.form ? inputChanges : lastChanges
      const { currentInboxPlan, newInboxPlan } = changes

      if (
        currentInboxPlan.planId !== newInboxPlan.planId &&
        isPricingSmaller(currentInboxPlan, newInboxPlan) &&
        isLimitsExceeded(newInboxPlan, usage.inbox)
      ) {
        setModals({
          downgradeKb: false,
          downgradeInbox: false,
          cancellation: false,
          limitKb: false,
          limitInbox: true,
        })
      } else {
        checkKbLimitAndSave(changes)
      }
    },
    [usage, checkKbLimitAndSave, lastChanges]
  )

  const cancelAccount = useCallback(
    ({ lastCompletedStep, responses }) => {
      if (lastCompletedStep.triggerCancelAccount) {
        dispatch(doCancelSubscription(responses))
      }
    },
    [dispatch]
  )

  const closeCardFailureModal = useCallback(
    () => {
      dispatch(doCloseCardFailureError())
    },
    [dispatch]
  )

  const startCancellationFlow = useCallback(() => {
    setModals({
      downgradeKb: false,
      downgradeInbox: false,
      cancellation: true,
      limitKb: false,
      limitInbox: false,
    })
  }, [])

  return {
    modals,
    cardError,
    checkInboxLimitAndSave,
    checkKbLimitAndSave,
    checkInboxAndSave,
    checkKbAndSave,
    saveChanges,
    cancelSave,
    cancelAccount,
    closeCardFailureModal,
    startCancellationFlow,
  }
}
